import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Users,
  Clock,
  Settings,
  BarChart3,
  Calendar,
  Zap,
  Layers,
  Search,
  MessageSquare,
  FileText,
} from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import Footer from "../components/Footer";

const CASE_SUMMARY = [
  {
    icon: Building2,
    title: "Industry",
    desc: "Healthcare (Operations)",
  },
  {
    icon: Users,
    title: "Key Users",
    desc: "COO, unit Managers, Patient Access Leads, Admin Staff, IT Teams",
  },
  {
    icon: Clock,
    title: "Business Need",
    desc: "Smoother patient flow and less administrative drag",
  },
  {
    icon: Settings,
    title: "Solution",
    desc: "Simpel Healthcare Operations Copilot",
  },
  {
    icon: BarChart3,
    title: "Impact",
    desc: "30-50% reduction in admin time",
  },
  {
    icon: Calendar,
    title: "Timeframe",
    desc: "10-16 months payback",
  },
];

const WHO_BENEFITS_DATA = [
  {
    role: "COO / Head of Operations",
    improves:
      "Clearer throughput and fewer hidden delays. Bottlenecks are visible earlier; capacity is used more across system the pilot scope.",
  },
  {
    role: "Unit / Service Manager",
    improves:
      "Smoother day-to-day coordination. Hand-offs and status are easier to track; repetitive admin takes less of the team's time.",
  },
  {
    role: "Bed / Flow / Patient Access Lead",
    improves:
      "Faster identification of flow constraints. Teams act on bottlenecks with better timing; pathway progress is easier to follow.",
  },
  {
    role: "Administrative / Support Staff",
    improves:
      "Less repetitive data work and chasing. More capacity for complex or patient-facing tasks that need a person.",
  },
  {
    role: "CIO / IT / Information Security",
    improves:
      "Controlled integration with operational systems. Role-based access, auditability and privacy by design.",
  },
];

const RESULTS_DATA = [
  {
    icon: Clock,
    subtitle: "Time spent on repetitive administrative tasks (pilot workflows)",
    title: "30% to 50% reduction",
  },
  {
    icon: BarChart3,
    subtitle: "Time to identify and act on operational bottlenecks",
    title: "40% to 60% faster",
  },
  {
    icon: Settings,
    subtitle: "Pathway / process cycle time on pilot scope",
    title: "10% to 25% improvement",
  },
  {
    icon: Users,
    subtitle: "Staff capacity redirected to higher-value work",
    title: "Clear gain on pilot teams",
  },
  {
    icon: Calendar,
    subtitle: "Typical payback window",
    title: "10 - 16 months",
  },
  {
    icon: Layers,
    subtitle: "First-year value (flow + admin efficiency)",
    title: "$180k - $480k (scale-dependent)",
  },
];


const PROCESS_STEPS_OPERATIONS = [
  {
    id: 1,
    icon: MessageSquare,
    title: "Identify",
    desc: "Spot bottlenecks and workflow issues.",
    bgGradient: "bg-gradient-to-br from-[#93C5FD] to-[#3B82F6]",
    glow: "shadow-[0_0_24px_rgba(59,130,246,0.35)]",
  },
  {
    id: 2,
    icon: Search,
    title: "Analyze",
    desc: "Understand the cause using operational data.",
    bgGradient: "bg-gradient-to-br from-[#E9D5FF] to-[#A855F7]",
    glow: "shadow-[0_0_24px_rgba(168,85,247,0.35)]",
  },
  {
    id: 3,
    icon: FileText,
    title: "Recommend",
    desc: "Get actionable next steps and automation opportunities.",
    bgGradient: "bg-gradient-to-br from-[#FEF08A] to-[#EAB308]",
    glow: "shadow-[0_0_24px_rgba(234,179,8,0.35)]",
  },
  {
    id: 4,
    icon: Settings,
    title: "Take action",
    desc: "Coordinate tasks and streamline admin work.",
    bgGradient: "bg-gradient-to-br from-[#BBF7D0] to-[#22C55E]",
    glow: "shadow-[0_0_24px_rgba(34,197,94,0.35)]",
  },
  {
    id: 5,
    icon: BarChart3,
    title: "Improve",
    desc: "Keep patient flow moving and scale across more units.",
    bgGradient: "bg-gradient-to-br from-[#FBCFE8] to-[#EC4899]",
    glow: "shadow-[0_0_24px_rgba(236,72,153,0.35)]",
  },
];

const HealthcareOperationsCopilot = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/ai-opportunities?tab=Industries");
    }
  };

  // Scroll tracking across sticky hero track
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const overlayY = useTransform(
    scrollYProgress,
    [0, 0.22, 0.75, 0.98],
    ["100%", "0%", "0%", "-100%"]
  );

  const overlayOpacity = useTransform(
    scrollYProgress,
    [0, 0.06, 0.22, 0.75, 0.95, 1],
    [0, 0.6, 1, 1, 0.7, 0]
  );

  const handleScrollDown = () => {
    const el = document.getElementById("case-at-a-glance");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else if (containerRef.current) {
      const top = containerRef.current.offsetTop;
      const height = containerRef.current.offsetHeight;
      window.scrollTo({
        top: top + height,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#000000] flex flex-col text-white">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 w-full bg-[#000000] border-b border-white/[0.06] shadow-sm">
        <div className="mx-auto flex h-[76px] sm:h-[82px] max-w-[1440px] items-center justify-between px-6 lg:px-[70px]">
          {/* Simpel Brand Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 sm:gap-3.5 group select-none shrink-0"
          >
            <img
              src="/images/simpel-icon-mark.png"
              alt="Simpel"
              className="h-[34px] sm:h-[38px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="flex flex-col justify-center">
              <span className="text-[22px] sm:text-[25px] font-bold leading-none tracking-tight text-white">
                Simpel
              </span>
              <span className="mt-[3px] text-[7.5px] sm:text-[8px] font-bold uppercase tracking-[2.2px] sm:tracking-[2.5px] text-white/70">
                AUTOMATION SIMPLIFIED
              </span>
            </div>
          </Link>

          {/* Right Breadcrumb & Back Button */}
          <div className="flex items-center gap-5 sm:gap-8">
            <div className="hidden sm:block border-b-2 border-[#2F6DF6] pb-0.5">
              <span className="text-[14px] sm:text-[15px] font-medium text-[#2F6DF6] tracking-wide select-none">
                Industries / Healthcare
              </span>
            </div>

            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-full bg-[#00D2EE] px-5 py-2 sm:px-6 sm:py-2.5 text-[13px] sm:text-[14px] font-bold text-black transition-all duration-200 hover:brightness-105 hover:shadow-[0_4px_16px_rgba(0,210,238,0.35)] active:scale-95 cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 stroke-[2.5]" />
              <span>Back to Cognitive AI</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 w-full bg-[#000000]">
        {/* =========================================================================
            Hero Section with Scroll Pinning Animation (matching Screenshot 1)
            Background: /images/insights/healthcare-operations-hero.jpg
           ========================================================================= */}
        <section ref={containerRef} className="relative w-full h-[270vh]">
          {/* Sticky Viewport Stage */}
          <div className="sticky top-[76px] sm:top-[82px] h-[calc(100vh-76px)] sm:h-[calc(100vh-82px)] w-full overflow-hidden bg-[#000000]">
            {/* Underlying Hero Section Photo - 100% crystal clear at scroll 0 */}
            <div className="absolute inset-0 w-full h-full">
              <img
                src="/images/insights/healthcare-operations-hero.jpg"
                alt="Healthcare Operations Copilot - Patient Flow Overview"
                className="w-full h-full object-cover object-center block select-none"
              />
            </div>

            {/* Frosted Blur Overlay Section with Pinning Animation */}
            <motion.div
              style={{
                y: overlayY,
                opacity: overlayOpacity,
              }}
              className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-20 text-center"
            >
              {/* Frosted Glass Background Tint matching Screenshot 1 */}
              <div
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{
                  backgroundColor: "rgba(15, 27, 41, 0.94)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                }}
              />

              {/* Foreground Content */}
              <div className="relative z-10 max-w-[1080px] flex flex-col items-center pt-4 sm:pt-0">
                {/* Category Tag matching Screenshot 1 */}
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[3px] text-white/70 mb-5 sm:mb-6">
                  HEALTHCARE - OPERATIONS COPILOT
                </p>

                {/* Main Heading matching Screenshot 1 */}
                <h1 className="text-[32px] sm:text-[44px] md:text-[50px] lg:text-[54px] font-bold leading-[1.2] sm:leading-[1.16] text-white tracking-tight">
                  Health care Operations Copilot:{" "}
                  <span className="bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent">
                    Smoother
                  </span>
                  <span className="block mt-1 sm:mt-1.5 bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent">
                    flow, cleaner bottlenecks, less admin drag
                  </span>
                </h1>

                {/* Subtitle Paragraph (3 Lines matching Screenshot 1) */}
                <div className="mt-5 sm:mt-6 max-w-[850px] text-[13.5px] sm:text-[14.5px] md:text-[15px] leading-[24px] sm:leading-[26px] text-white/80 text-center">
                  <p className="whitespace-normal sm:whitespace-nowrap">
                    How Simpel Healthcare Operations Copilot helps teams coordinate workflows,
                  </p>
                  <p className="whitespace-normal sm:whitespace-nowrap mt-0.5">
                    identify bottlenecks and reduce repetitive administrative work — so operations
                  </p>
                  <p className="whitespace-normal sm:whitespace-nowrap mt-0.5">
                    support care instead of competing with it.
                  </p>
                </div>

                {/* 4 Feature Pills in a horizontal row matching Screenshot 1 */}
                <div className="mt-8 sm:mt-10 flex flex-wrap items-center justify-center gap-6 sm:gap-10 max-w-[980px]">
                  {/* Pill 1 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <Zap className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Visibility across<br className="hidden sm:inline" /> workflows
                    </span>
                  </div>

                  {/* Pill 2 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <BarChart3 className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Faster Bottleneck<br className="hidden sm:inline" /> Resolution
                    </span>
                  </div>

                  {/* Pill 3 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <Users className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Less administrative<br className="hidden sm:inline" /> effort
                    </span>
                  </div>

                  {/* Pill 4 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <Clock className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Smoother patient<br className="hidden sm:inline" /> flow
                    </span>
                  </div>
                </div>

                {/* Vertical Dashed Line with Downward Arrow */}
                <div
                  onClick={handleScrollDown}
                  className="mt-10 sm:mt-12 flex flex-col items-center cursor-pointer group"
                  title="Scroll to explore"
                >
                  <svg
                    width="10"
                    height="120"
                    viewBox="0 0 10 120"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="opacity-75 transition-opacity group-hover:opacity-100"
                  >
                    <line
                      x1="5"
                      y1="0"
                      x2="5"
                      y2="110"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeDasharray="3 3"
                    />
                    <path
                      d="M5 110L10 102M5 110L0 102"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* =========================================================================
            1. Case at a glance Section (matching Screenshot 3)
            Background: rgba(245, 250, 254, 1)
            6 Overview Cards (2 rows x 3 cols)
           ========================================================================= */}
        <section
          id="case-at-a-glance"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-8 sm:mb-10">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Case at a glance
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* 6 Cards Grid (3 cols x 2 rows) matching Screenshot 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {CASE_SUMMARY.map((card, idx) => {
                const Icon = card.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-4 sm:gap-5 rounded-[14px] sm:rounded-[16px] bg-white border border-[#EAF2FA] p-6 sm:p-7 shadow-[0_2px_12px_rgba(15,23,42,0.02)] transition-all duration-200 hover:shadow-[0_8px_24px_rgba(15,23,42,0.05)] hover:-translate-y-0.5"
                  >
                    {/* Circular Blue Icon Badge */}
                    <div className="flex h-13 w-13 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                      <Icon className="h-6 w-6 stroke-[2]" />
                    </div>

                    {/* Text Details */}
                    <div className="flex flex-col gap-1.5 pt-0.5">
                      <span className="text-[12px] sm:text-[13px] font-bold uppercase tracking-wider text-[#0F172A]">
                        {card.title}
                      </span>
                      <p className="text-[13.5px] sm:text-[14px] leading-[21px] text-[#64748B]">
                        {card.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            2. The Context Section (matching Screenshot 1)
            Background: /images/insights/healthcare-context-bg.jpg
            Left: Deep dark gradient overlay with heading & narrative text
           ========================================================================= */}
        <section
          id="the-context"
          className="relative w-full overflow-hidden bg-[#050B14] min-h-[320px] sm:h-[340px] lg:h-[350px] flex items-center text-white"
        >
          {/* Base Background Image: Full span with right-aligned focus */}
          <div className="absolute inset-0 w-full h-full pointer-events-none select-none overflow-hidden">
            <img
              src="/images/insights/healthcare-context-bg.jpg"
              alt="Healthcare context - Nurse at clinical workstation"
              className="h-full w-full object-cover object-[75%_center] lg:object-right"
            />
          </div>

          {/* Left Solid Dark Base & Progressive Gradient Overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #050B14 0%, #050B14 36%, rgba(5, 11, 20, 0.95) 48%, rgba(5, 11, 20, 0.45) 66%, rgba(5, 11, 20, 0) 84%)",
            }}
          />

          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-10 sm:py-12 lg:py-0">
            <div className="max-w-[620px] lg:max-w-[660px]">
              {/* Header row with horizontal divider line matching Screenshot 1 */}
              <div className="flex items-center gap-6 mb-5 sm:mb-6">
                <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                  The Context
                </h2>
                <div className="flex-1 h-[1px] bg-[#CEDDF1]/30" />
              </div>

              {/* Paragraph text matching Screenshot 1 */}
              <p className="text-[13px] sm:text-[14px] lg:text-[14.5px] leading-[23px] sm:leading-[25px] text-white/85">
                Healthcare runs on coordinated workflows: scheduling, admissions, transfers, discharge, supply and many handoffs between them. When teams can see where work is building up, who owns the next step and which tasks are repetitive, flow improves, capacity is used where in matters and staff spend more time on patients.
              </p>
            </div>
          </div>
        </section>

        {/* =========================================================================
            3. What the solution does Section (matching Screenshot 1 & Figma specs: 676px x 308px, Radius 12px, Shadow 4px 4px 24px rgba(0,0,0,0.25))
            Background: rgba(245, 250, 254, 1)
           ========================================================================= */}
        <section
          id="what-the-solution-does"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-12 justify-between">
              {/* Left Column: Solution Monitor Card matching Figma specs (676px x 308px, Radius 12px, Shadow 4px 4px 24px rgba(0,0,0,0.25)) */}
              <div className="w-full lg:w-[676px] shrink-0 flex justify-center lg:justify-start">
                <div
                  style={{
                    boxShadow: "4px 4px 24px 0px rgba(0, 0, 0, 0.25)",
                    borderRadius: "12px",
                  }}
                  className="w-full max-w-[676px] h-[240px] sm:h-[280px] lg:h-[308px] overflow-hidden rounded-[12px] bg-white border border-[#E2EDF8]"
                >
                  <img
                    src="/images/insights/healthcare-solution-monitor.jpg"
                    alt="Healthcare Operations Copilot software monitor"
                    className="w-full h-full object-cover block select-none"
                  />
                </div>
              </div>

              {/* Right Column: Narrative Details */}
              <div className="flex-1 max-w-[560px] flex flex-col justify-center">
                {/* Header row with horizontal line matching Screenshot */}
                <div className="flex items-center gap-6 mb-6 sm:mb-7">
                  <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    What the solution does
                  </h2>
                  <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
                </div>

                <p className="text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[24px] sm:leading-[26px] text-[#64748B]">
                  Operations and clinical support teams get intelligent assistance across daily workflows. The Copilot helps coordinate steps, highlight bottlenecks and automate or streamline repetitive administrative tasks — status checks, routine follow-ups and standard data assembly — within approved rules.
                </p>

                <p className="mt-5 sm:mt-6 text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[24px] sm:leading-[26px] text-[#64748B]">
                  Insights sit alongside existing systems with privacy, access and clinical governance requirements built in from the start. The Copilot supports people and processes; it does not replace clinical or managerial judgement.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            4. The challenge Section (matching Screenshot 3)
            Background: #FFFFFF (White)
            Left: The challenge heading + narrative paragraph
            Right: 2 Numbered Problem Cards (01, 02)
           ========================================================================= */}
        <section
          id="the-challenge"
          className="w-full py-16 sm:py-20 lg:py-24 bg-white text-slate-900"
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              {/* Left Column: Heading + Narrative */}
              <div className="lg:col-span-6">
                <div className="flex items-center gap-6 mb-6 sm:mb-7">
                  <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    The challenge
                  </h2>
                  <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
                </div>

                <p className="text-[13.5px] sm:text-[14px] leading-[23px] sm:leading-[25px] text-[#64748B]">
                  Healthcare operations involve many handoffs and depend on timely, accurate information. The challenge is to make bottlenecks visible early and reduce repetitive administrative work so teams can focus on patient care.
                </p>
              </div>

              {/* Right Column: 2 Numbered Problem Cards */}
              <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                {/* Card 01 */}
                <div className="rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-6 sm:p-7 flex flex-col justify-between transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
                  <div>
                    <span className="text-[26px] sm:text-[28px] font-bold text-[#00D2EE] tracking-tight leading-none">
                      01
                    </span>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] mt-3.5 mb-2 leading-snug">
                      Fragmented workflows
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      Information is spread across systems, making it difficult to track the next step.
                    </p>
                  </div>
                </div>

                {/* Card 02 */}
                <div className="rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-6 sm:p-7 flex flex-col justify-between transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
                  <div>
                    <span className="text-[26px] sm:text-[28px] font-bold text-[#00D2EE] tracking-tight leading-none">
                      02
                    </span>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] mt-3.5 mb-2 leading-snug">
                      Admin-heavy processes
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      Teams spend significant time on repetitive tasks and status checks.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            5. Who benefits Section (matching Screenshot & Figma specs)
            Background: /images/insights/healthcare-operations-hero.jpg
            Table: Fill 1,280px, Radius 12px, rgba(9, 21, 40, 0.6), blur 20px
            Header: Height 49px, rgba(14, 29, 53, 1), borderBottom rgba(26, 46, 76, 1)
           ========================================================================= */}
        <section
          id="who-benefits"
          className="relative w-full overflow-hidden bg-[#020B18] py-16 sm:py-20 lg:py-24 text-white"
        >
          {/* Base Background Image: Healthcare Operations Hero Photo */}
          <div className="absolute inset-0 w-full h-full select-none pointer-events-none">
            <img
              src="/images/insights/healthcare-operations-hero.jpg"
              alt="Who Benefits Background"
              className="h-full w-full object-cover object-center"
            />
          </div>

          {/* Dark Glass Overlay matching Screenshot */}
          <div className="absolute inset-0 bg-[#020B18]/60 backdrop-blur-[2px] pointer-events-none" />

          <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with gradient text and horizontal divider */}
            <div className="flex items-center gap-6 mb-3 sm:mb-4">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Who benefits
              </h2>
              <div className="flex-1 h-[1px] bg-[#CEDDF1]/40" />
            </div>

            {/* Subtitle */}
            <p className="text-[13.5px] sm:text-[14.5px] leading-[22px] text-white/80 mb-8 sm:mb-10 max-w-[840px]">
              Five roles gain when operations flow better.
            </p>

            {/* Roles Table Container matching exact Figma specs: rgba(9, 21, 40, 0.6), blur 20px, radius 12px, border rgba(26, 46, 76, 1) */}
            <div
              style={{
                backgroundColor: "rgba(9, 21, 40, 0.28)",
                borderColor: "rgba(26, 46, 76, 1)",
                borderWidth: "1px",
                borderStyle: "solid",
                borderRadius: "12px",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
              }}
              className="w-full max-w-[1280px] mx-auto rounded-[12px] overflow-hidden"
            >
              {/* Table Header: Height 49px, rgba(14, 29, 53, 1), borderBottom rgba(26, 46, 76, 1) */}
              <div
                style={{
                  backgroundColor: "rgba(14, 29, 53, 0.65)",
                  borderBottomColor: "rgba(26, 46, 76, 1)",
                }}
                className="grid grid-cols-12 px-6 h-[49px] items-center border-b text-[11px] sm:text-[12px] font-bold tracking-[1.5px] uppercase text-white/90"
              >
                <div className="col-span-12 sm:col-span-4 lg:col-span-4">Role</div>
                <div className="hidden sm:block sm:col-span-8 lg:col-span-8">What improves for them</div>
              </div>

              {/* Table Rows with divider matching rgba(26, 46, 76, 1) */}
              <div className="divide-y" style={{ borderColor: "rgba(26, 46, 76, 1)" }}>
                {WHO_BENEFITS_DATA.map((item) => (
                  <div
                    key={item.role}
                    style={{ borderColor: "rgba(26, 46, 76, 1)" }}
                    className="grid grid-cols-12 px-6 py-4 sm:py-5 items-start gap-2 sm:gap-6 hover:bg-white/[0.04] transition-colors"
                  >
                    <div className="col-span-12 sm:col-span-4 lg:col-span-4 text-[14px] sm:text-[15px] font-bold text-white leading-snug">
                      {item.role}
                    </div>
                    <div className="col-span-12 sm:col-span-8 lg:col-span-8 text-[13px] sm:text-[13.5px] leading-[22px] text-white/80">
                      <span className="sm:hidden text-[11px] uppercase tracking-wider text-slate-400 font-semibold block mb-1">
                        What improves:
                      </span>
                      {item.improves}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            6. Results organizations typically see Section (matching Screenshot 5)
            Background: rgba(245, 250, 254, 1)
            6 Metric Cards (3 cols x 2 rows)
           ========================================================================= */}
        <section
          id="results-organizations-typically-see"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-8 sm:mb-10">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Results organizations typically see
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* 6 Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {RESULTS_DATA.map((card, idx) => {
                const Icon = card.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-4 sm:gap-5 rounded-[14px] sm:rounded-[16px] bg-white border border-[#EAF2FA] p-6 sm:p-7 shadow-[0_2px_12px_rgba(15,23,42,0.02)] transition-all duration-200 hover:shadow-[0_8px_24px_rgba(15,23,42,0.05)] hover:-translate-y-0.5"
                  >
                    {/* Circular Blue Icon Badge */}
                    <div className="flex h-13 w-13 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                      <Icon className="h-6 w-6 stroke-[2]" />
                    </div>

                    {/* Text Details */}
                    <div className="flex flex-col gap-1.5 pt-0.5">
                      <span className="text-[12.5px] sm:text-[13px] leading-[19px] text-[#64748B]">
                        {card.subtitle}
                      </span>
                      <h3 className="text-[15.5px] sm:text-[16.5px] font-bold text-[#0F172A] leading-snug">
                        {card.title}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        
        {/* =========================================================================
            7. How it works in practice Section (matching Screenshot 1)
            Background: #FFFFFF (White)
            5-Step Process Flow: Identify -> Analyze -> Recommend -> Take action -> Improve
            Bottom: Head of Operations Quote Box
           ========================================================================= */}
        <section
          id="how-it-works-in-practice"
          className="w-full py-16 sm:py-20 lg:py-24 bg-white text-slate-900"
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-3 sm:mb-4">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                How it works in practice
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* Subtitle matching Screenshot 1 */}
            <p className="text-[13.5px] sm:text-[14px] lg:text-[14.5px] leading-[23px] text-[#64748B] mb-12 sm:mb-16 max-w-[1040px]">
              From workflow insight to action — helping teams identify bottlenecks, reduce administrative effort and keep patient flow moving.
            </p>

            {/* 5-Step Process Flow with Connectors */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-2">
              {PROCESS_STEPS_OPERATIONS.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={step.id}>
                    {/* Step Item */}
                    <div className="flex flex-col items-center text-center max-w-[210px] w-full group">
                      {/* Colored Circle Badge */}
                      <div
                        className={`flex h-16 w-16 sm:h-18 sm:w-18 items-center justify-center rounded-full ${step.bgGradient} ${step.glow} text-white transition-transform duration-300 group-hover:scale-110 mb-4 sm:mb-5`}
                      >
                        <Icon className="h-7 w-7 sm:h-8 sm:w-8 stroke-[2.2]" />
                      </div>

                      {/* Step Title */}
                      <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] mb-2">
                        {step.title}
                      </h3>

                      {/* Step Description */}
                      <p className="text-[12.5px] sm:text-[13px] leading-[19px] text-[#64748B]">
                        {step.desc}
                      </p>
                    </div>

                    {/* Connecting Arrow between steps (hidden on last step) */}
                    {idx < PROCESS_STEPS_OPERATIONS.length - 1 && (
                      <div className="hidden lg:flex items-center justify-center shrink-0 px-2 text-[#00D2EE]">
                        <ArrowRight className="h-6 w-6 stroke-[2.5]" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Quote Callout Box matching Screenshot 1 */}
            <div className="mt-12 sm:mt-16 w-full rounded-[14px] sm:rounded-[18px] bg-[#F5FAFE] border border-[#E2EDF8] py-6 sm:py-7 px-6 sm:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 shadow-[0_2px_12px_rgba(15,23,42,0.02)]">
              <div className="flex items-start gap-3.5">
                <span className="text-[#007AFF] text-[32px] sm:text-[40px] font-serif leading-none select-none -mt-1">
                  &ldquo;
                </span>
                <p className="text-[14.5px] sm:text-[15.5px] font-medium text-[#1E293B] leading-[25px] sm:leading-[27px]">
                  &ldquo;We did not need more meetings about flow. We needed to see where work was stuck and cut the admin that did not need a person. The Copilot gave us both — and the pathway moved faster.&rdquo;
                </p>
              </div>

              <div className="shrink-0 lg:border-l-2 lg:border-[#007AFF] lg:pl-6 pt-4 lg:pt-0 border-t lg:border-t-0 border-[#E2EDF8]">
                <span className="block text-[14px] sm:text-[14.5px] font-bold text-[#0F172A] leading-tight">
                  Head of Operations
                </span>
                <span className="block text-[12px] sm:text-[12.5px] text-[#64748B] mt-1">
                  Healthcare Organization
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            8. CTA Banner Section (matching Screenshot & Figma specs)
            Background: /images/insights/healthcare-cta-corridor.jpg
            Heading: "Turn Operations into better patient flow." in 1 single line without clipping
           ========================================================================= */}
        <section
          id="cta-banner"
          className="relative w-full overflow-hidden bg-[#050B14] flex items-center min-h-[340px] lg:h-[340px] text-white"
        >
          {/* Base Background Image: Full span with right-aligned focus so left edge NEVER shows */}
          <div className="absolute inset-0 w-full h-full pointer-events-none select-none overflow-hidden">
            <img
              src="/images/insights/healthcare-cta-corridor.jpg"
              alt="Turn Operations into better patient flow"
              className="h-full w-full object-cover object-[80%_center] lg:object-right"
            />
          </div>

          {/* Linear Gradient Overlay: 100% solid #050B14 on the left, smoothly fading out to the right */}
          <div
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #050B14 0%, #050B14 36%, rgba(5, 11, 20, 0.95) 48%, rgba(5, 11, 20, 0.45) 66%, rgba(5, 11, 20, 0) 84%)",
            }}
          />

          {/* Figma Progressive Blur Layer: 45.73% to 64.69%, start blur 14, end blur 0 */}
          <div
            className="absolute inset-y-0 left-0 w-[68%] pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, rgba(5, 11, 20, 0.8) 0%, rgba(5, 11, 20, 0.5) 45.73%, rgba(5, 11, 20, 0) 64.69%)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              maskImage: "linear-gradient(to right, black 45.73%, transparent 64.69%)",
              WebkitMaskImage: "linear-gradient(to right, black 45.73%, transparent 64.69%)",
            }}
          />

          {/* Content Container (Max width 1440px matching layout) */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-10 sm:py-12 lg:py-0 flex flex-col justify-center">
            <div className="max-w-[800px] xl:max-w-[880px] w-full">
              {/* Heading: exact 1 line on desktop */}
              <h2 className="text-[24px] sm:text-[27px] md:text-[30px] lg:text-[32px] xl:text-[34px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent leading-[1.25] whitespace-normal sm:whitespace-nowrap inline-block">
                Turn Operations into better patient flow.
              </h2>

              {/* Subtitle */}
              <p className="mt-3.5 sm:mt-4 text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[22px] sm:leading-[24px] text-white/85 max-w-[620px]">
                See how Simpel Healthcare Operations Copilot can help your teams reduce administrative drag and improve flow.
              </p>

              {/* Consultation Button */}
              <div className="mt-6 sm:mt-7">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-2.5 sm:px-7 sm:py-3 text-[13.5px] sm:text-[14px] font-bold text-[#0F172A] transition-all duration-200 hover:bg-slate-100 hover:shadow-[0_8px_24px_rgba(255,255,255,0.25)] hover:scale-105 active:scale-95"
                >
                  <span>Request a Consultation</span>
                  <ArrowRight className="h-4 w-4 stroke-[2.5]" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Anchor for future sections */}
        <div id="content-start" />
      </main>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default HealthcareOperationsCopilot;
